import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import { Calendar, ExternalLink } from "lucide-react";
import { format, parseISO } from "date-fns";
import { de, enUS } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsPage() {
  const { t, language } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/public/news`);
      setNews(response.data);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d. MMMM yyyy", { 
        locale: language === "de" ? de : enUS 
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <Header />

      <main className="px-6 md:px-12 py-12 max-w-4xl mx-auto">
        <h1 className="font-outfit text-3xl sm:text-4xl font-medium text-forest-900 mb-3">
          {t("news.title")}
        </h1>
        <p className="text-lg text-stone-600 mb-10">
          {t("news.subtitle")}
        </p>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-forest-800 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : news.length === 0 ? (
          <Card className="bg-white rounded-xl border-stone-200">
            <CardContent className="p-8 text-center">
              <p className="text-stone-500">{t("news.noNews")}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {news.map((item) => (
              <Card key={item.id} className="bg-white rounded-xl border-stone-200 overflow-hidden hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-sm text-stone-500 mb-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(item.created_at)}
                  </div>
                  <h2 className="font-outfit text-lg font-medium text-forest-900 mb-2">
                    {language === "de" ? item.title_de : item.title_en}
                  </h2>
                  <p className="text-stone-600 leading-relaxed mb-4">
                    {language === "de" ? item.description_de : item.description_en}
                  </p>
                  {item.link && (
                    <a 
                      href={item.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-forest-700 hover:text-forest-900 text-sm font-medium"
                    >
                      {t("news.readMore")}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 px-6 md:px-12 border-t border-stone-200 mt-12">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <p className="text-stone-500 text-xs">© 2026 Universität Trier. Learning Brain Lab</p>
        </div>
      </footer>
    </div>
  );
}
